import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="ACCESS TERMINAL" sub="Sign in to your Interstate PC account" testId="login-page">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="font-mono text-xs tracking-widest uppercase text-zinc-400">Email</Label>
          <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
        </div>
        <div>
          <Label className="font-mono text-xs tracking-widest uppercase text-zinc-400">Password</Label>
          <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
        </div>
        <Button data-testid="login-submit" type="submit" disabled={loading}
          className="w-full h-11 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">
          {loading ? "SIGNING IN..." : "SIGN IN"}
        </Button>
      </form>
      <div className="text-sm text-zinc-400 text-center mt-6">
        New here? <Link to="/register" className="text-[#FF5C00] hover:underline">Create an account</Link>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form.email, form.password, form.name);
      toast.success("Account created");
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="CREATE ACCOUNT" sub="Join the Interstate crew" testId="register-page">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="font-mono text-xs tracking-widest uppercase text-zinc-400">Name</Label>
          <Input data-testid="reg-name" required value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
            className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
        </div>
        <div>
          <Label className="font-mono text-xs tracking-widest uppercase text-zinc-400">Email</Label>
          <Input data-testid="reg-email" type="email" required value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
            className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
        </div>
        <div>
          <Label className="font-mono text-xs tracking-widest uppercase text-zinc-400">Password (6+ chars)</Label>
          <Input data-testid="reg-password" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({...form, password: e.target.value})}
            className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
        </div>
        <Button data-testid="reg-submit" type="submit" disabled={loading}
          className="w-full h-11 bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">
          {loading ? "CREATING..." : "CREATE ACCOUNT"}
        </Button>
      </form>
      <div className="text-sm text-zinc-400 text-center mt-6">
        Already have one? <Link to="/login" className="text-[#FF5C00] hover:underline">Sign in</Link>
      </div>
    </AuthShell>
  );
}

function AuthShell({ title, sub, children, testId }) {
  return (
    <div className="container-tight py-16 flex justify-center" data-testid={testId}>
      <div className="w-full max-w-md tactical-border border border-zinc-800 bg-[#0a0a0c] p-8 rounded-sm">
        <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-2">// {sub}</div>
        <h1 className="font-display font-black text-3xl mb-6">{title}</h1>
        {children}
      </div>
    </div>
  );
}
